<section class="market-admin-dashboard" aria-label="Manager Dashboard">
  <div class="welcome">
    <h1>Welcome</h1>
    <div class="user-name" [title]="userName">{{userName}}</div>
    <p>Take action on Available Cycles, View Past Cycles, and manage actions related to your Roles</p>

    @if (!isLoading && error) {
      <div class="error-message">
        <mat-icon>error</mat-icon>
        {{ error }}
        <button mat-icon-button color="warn" [appBefairTooltip]="'Retry loading data'" (click)="refreshData()">
          <mat-icon>refresh</mat-icon>
        </button>
      </div>
    }

    <div class="meta" role="list">
      <span class="pill" role="listitem">
        <mat-icon class="pill-icon">location_on</mat-icon>
        Market: {{ market }}
      </span>
      <span class="pill" role="listitem">
        <mat-icon class="pill-icon">business</mat-icon>
        Business Unit: {{ businessUnit }}
      </span>
    </div>

    <div class="cycles-container">
      <!-- Active Cycles Card -->
      <div class="card cycles-card">
        <div class="section-header">
          <h3 class="section-title">
            <mat-icon>schedule</mat-icon>
            Active Cycles
          </h3>
          <span class="section-count">{{ activeCycles.length }}</span>
        </div>
        <div class="cycles-list">
          @if (isLoading) {
            <div class="loading-state">
              <p>Loading active cycles...</p>
            </div>
          } @else if (activeCycles.length === 0) {
            <div class="empty-state">
              <mat-icon>schedule</mat-icon>
              <p>No active cycles found</p>
            </div>
          } @else {
            @for (cycle of activeCycles; track cycle.cycle_id) {
              <div class="cycle-item active-cycle" tabindex="0" (click)="onOpenCycle(cycle.cycle_id)" (keyup.enter)="onOpenCycle(cycle.cycle_id)">
                <div class="cycle-header">
                  <span class="status-badge status-active">
                    <mat-icon>radio_button_checked</mat-icon>
                    ACTIVE
                  </span>
                  @if (cycle.action_required_count && cycle.action_required_count > 0) {
                    <span class="action-badge" [appBefairTooltip]="cycle.action_required_count + (cycle.action_required_count === 1 ? ' action required' : ' actions required')">
                      <mat-icon>notification_important</mat-icon>
                      <span class="badge-count">{{ cycle.action_required_count }}</span>
                    </span>
                  }
                </div>
                <div class="cycle-body">
                  <h4 class="cycle-title" [title]="cycle.cycle_name">{{ cycle.cycle_name }}</h4>
                  <div class="cycle-meta">
                    <div class="meta-item">
                      <mat-icon>event</mat-icon>
                      <span>Due: {{ cycle.due_date | date:'dd-MMM-yyyy' }}</span>
                    </div>
                  </div>
                  <div class="progress-section">
                    <div class="progress-info">
                      <span class="progress-text">Progress: {{ cycle.completed_assignments }}/{{ cycle.total_forms }} Forms</span>
                      <span class="progress-percent">{{ cycle.progress }}%</span>
                    </div>
                    <div class="progress-bar">
                      <div class="progress-fill" [style.width.%]="cycle.progress"></div>
                    </div>
                  </div>
                  <button type="button" class="action-link" (click)="onOpenCycle(cycle.cycle_id); $event.stopPropagation()">
                    <mat-icon>open_in_new</mat-icon>
                    Open Cycle
                  </button>
                </div>
              </div>
            }
          }
        </div>
      </div>

      <!-- Past Cycles Card -->
      <div class="card cycles-card">
        <div class="section-header">
          <h3 class="section-title">
            <mat-icon>history</mat-icon>
            Past Cycles
          </h3>
          <span class="section-count">{{ totalPastCycles }}</span>
        </div>
        <div class="cycles-list">
          @if (isLoading || isLoadingPastCycles) {
            <div class="loading-state">
              <p>Loading past cycles...</p>
            </div>
          } @else if (pastCycles.length === 0) {
            <div class="empty-state">
              <mat-icon>history</mat-icon>
              <p>No past cycles found</p>
            </div>
          } @else {
            @for (cycle of pastCycles; track cycle.cycle_id) {
              <div class="cycle-item past-cycle" tabindex="0" (click)="onViewCycle(cycle.cycle_id)" (keyup.enter)="onViewCycle(cycle.cycle_id)">
                <div class="cycle-header">
                  <span class="status-badge status-completed">
                    <mat-icon>check_circle</mat-icon>
                    COMPLETED
                  </span>
                  @if (cycle.action_required_count && cycle.action_required_count > 0) {
                    <span class="action-badge" [appBefairTooltip]="cycle.action_required_count + (cycle.action_required_count === 1 ? ' action required' : ' actions required')">
                      <mat-icon>notification_important</mat-icon>
                      <span class="badge-count">{{ cycle.action_required_count }}</span>
                    </span>
                  }
                </div>
                <div class="cycle-body">
                  <h4 class="cycle-title" [title]="cycle.cycle_name">{{ cycle.cycle_name }}</h4>
                  <div class="cycle-meta">
                    <div class="meta-item">
                      <mat-icon>event_available</mat-icon>
                      <span>Completed: {{ cycle.completed_date | date:'dd-MMM-yyyy' }}</span>
                    </div>
                  </div>
                  <button type="button" class="action-link" (click)="onViewCycle(cycle.cycle_id); $event.stopPropagation()">
                    <mat-icon>visibility</mat-icon>
                    View Cycle
                  </button>
                </div>
              </div>
            }
          }
        </div>

        <!-- Pagination -->
        @if (!isLoadingPastCycles && pastCycles.length > 0) {
          <div class="pagination-wrapper">
            <mat-paginator
              aria-label="Past cycles pagination"
              [length]="totalPastCycles"
              [pageSize]="pageSize"
              [pageIndex]="pageIndex"
              [pageSizeOptions]="pageSizeOptions"
              [showFirstLastButtons]="true"
              (page)="onPageChange($event)" />
          </div>
        }
      </div>
    </div>
  </div>

  <aside class="side" aria-label="Overview">
    <div>
      <div class="section-title"><mat-icon class="section-icon">task_alt</mat-icon>What you can do</div>
      <ul class="capability-list">
        <li>Review and submit forms for your assigned cycles</li>
        <li>Monitor progress and track completion status</li>
        <li>Manage your team and coordinate with sub-managers</li>
      </ul>
    </div>

    <div>
      <div class="section-title"><mat-icon class="section-icon">link</mat-icon>Quick links</div>
      <div class="list">
        @if (reportsDashboardUrl) {
          <a class="list-item" target="_blank" rel="noopener noreferrer" [href]="reportsDashboardUrl">
            <div class="icon icon-small i-brand" aria-hidden="true"><mat-icon>analytics</mat-icon></div>
            <div>
              <p class="title">Reports and Dashboard</p>
              <p class="sub">View analytics, metrics, and performance reports</p>
            </div>
          </a>
        }
      </div>
    </div>

    <div>
      <div class="section-title"><mat-icon class="section-icon">person</mat-icon>Need Help?</div>
      <div class="list">
        <a routerLink="/help-guide" class="list-item">
          <div class="icon icon-small i-neutral" aria-hidden="true"><mat-icon>help_outline</mat-icon></div>
          <div>
            <p class="title">Help Guide</p>
            <p class="sub">Access documentation, tutorials, and FAQs</p>
          </div>
        </a>

        <a routerLink="/contact-support" class="list-item">
          <div class="icon icon-small i-brand" aria-hidden="true"><mat-icon>support_agent</mat-icon></div>
          <div>
            <p class="title">Contact & Support</p>
            <p class="sub">Get in touch with our support team</p>
          </div>
        </a>
      </div>
    </div>

    <div>
      <div class="section-header">
        <div class="section-title">
          <mat-icon class="section-icon">people</mat-icon>
          Sub Managers
        </div>
        <span class="section-count">{{ subManagers.length }}</span>
      </div>

      <div class="managers-list">
        @if (isLoadingSubManagers) {
          <div class="loading-state">
            <mat-spinner diameter="40" />
            <p>Loading sub managers...</p>
          </div>
        } @else if (subManagers.length === 0) {
          <div class="empty-state">
            <mat-icon>people_outline</mat-icon>
            <p>No sub managers found</p>
          </div>
        } @else {
          @for (manager of subManagers; track manager.id) {
            <div class="manager-card"
                 role="button"
                 tabindex="0"
                 [appBefairTooltip]="&quot;Click to view &quot; + manager.first_name + &quot; &quot; + manager.last_name + &quot;'s cycles&quot;"
                 (click)="onViewSubManager(manager)"
                 (keyup.enter)="onViewSubManager(manager)">
              <div class="manager-avatar">
                @if (manager.profileUrl) {
                <img class="manager-avatar-img" width="40" height="40" appDefaultImage [userName]="manager.first_name + ' ' + manager.last_name" [ngSrc]="manager.profileUrl" [alt]="manager.first_name + ' ' + manager.last_name">
                } @else {
                  <div class="manager-avatar-initials">
                    {{ manager.first_name + ' ' + manager.last_name | appUserInitials }}
                  </div>
                }
              </div>
              <div class="manager-info">
                <p class="title manager-name">{{ manager.first_name }} {{ manager.last_name }}</p>
                <p class="sub manager-role">{{ manager.email }}</p>
              </div>
              <div class="manager-action">
                <mat-icon>chevron_right</mat-icon>
              </div>
            </div>
          }
        }
      </div>
    </div>
  </aside>
</section>
